/**
 * Wizard card for the limo_anywhere step (Slice 3).
 *
 * Two-phase, admin-driven, modeled on StripeConnectCard:
 *   1. Creds sub-form → invokes onboard-limo-anywhere `save_and_fetch`, which
 *      stores the LA credentials (plaintext account_settings, Option A) and
 *      re-fetches vehicle types via the existing Playwright automation. The
 *      task flips to needs_review with suggested mappings.
 *   2. Mapping table → admin reviews the suggestions and clicks "Confirm
 *      mapping", invoking `confirm_mapping`, which persists
 *      vehicles.limo_anywhere_vehicle_type_id and flips the task to done.
 *
 * The password is never echoed back; once stored the form shows "stored ✓" and
 * an empty password field on re-submit means "keep existing". Status auto-
 * refreshes via the run page's 5s poll.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, Loader2, RefreshCw, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/lib/supabase'
import type { OnboardingTask, Tables } from '@limo/database'
import { useInvokeOnboardingStep } from '@/hooks/useOnboarding'
import { StepCard } from './StepCard'

type LaVehicleTypeRow = Pick<
  Tables<'limo_anywhere_vehicle_types'>,
  'id' | 'name' | 'code'
>

interface LimoAnywhereCardProps {
  task: OnboardingTask
  accountId: string
  runId: string
}

type MatchConfidence = 'code' | 'name' | 'token' | 'none'

interface SuggestedMapping {
  vehicleId: string
  vehicleName: string
  laVehicleTypeDbId: string | null
  confidence: MatchConfidence
}

interface UnmappedLaType {
  dbId: string
  name: string
  code: string | null
}

interface LimoAnywhereResult {
  vehicleTypeCount?: number
  lastSyncedAt?: string
  suggestedMappings?: SuggestedMapping[]
  unmappedLaTypes?: UnmappedLaType[]
  passwordStored?: boolean
  hasVehicles?: boolean
  mappedCount?: number
  createdCount?: number
  mappingConfirmedAt?: string
}

const credsSchema = z.object({
  company: z.string().trim().min(1, 'Company ID is required'),
  username: z.string().trim().min(1, 'Username is required'),
  password: z.string().optional(),
  createAs: z.enum(['reservation', 'quote']),
  pointToPoint: z.string().trim().optional(),
  hourly: z.string().trim().optional(),
  syncBookings: z.boolean(),
})

type CredsFormData = z.infer<typeof credsSchema>

const CONFIDENCE_HINT: Record<MatchConfidence, string> = {
  code: 'Matched by code',
  name: 'Matched by name',
  token: 'Suggested (similar name)',
  none: 'No suggestion — pick manually',
}

export function LimoAnywhereCard({
  task,
  accountId,
  runId,
}: LimoAnywhereCardProps) {
  const { toast } = useToast()
  const invokeStep = useInvokeOnboardingStep(runId)

  const result = (task.result ?? {}) as LimoAnywhereResult
  const isDone = task.status === 'done'
  const needsReview = task.status === 'needs_review'

  const form = useForm<CredsFormData>({
    resolver: zodResolver(credsSchema),
    defaultValues: {
      company: '',
      username: '',
      password: '',
      createAs: 'reservation',
      pointToPoint: '',
      hourly: '',
      syncBookings: true,
    },
  })

  // Read back the saved LA settings so the form survives a page refresh — the
  // step writes these to account_settings but the form needs hydrating on mount
  // (the password is never read back; we only surface that one is stored).
  const { data: savedSettings } = useQuery({
    queryKey: ['onboarding-la-settings', accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_settings')
        .select('key, value')
        .eq('account_id', accountId)
        .in('key', [
          'limo_anywhere_company',
          'limo_anywhere_username',
          'limo_anywhere_password',
          'limo_anywhere_create_as',
          'limo_anywhere_service_point_to_point',
          'limo_anywhere_service_hourly',
          'limo_anywhere_sync_bookings',
        ])
      if (error) throw new Error(error.message)
      const map: Record<string, string> = {}
      for (const row of data ?? []) {
        if (row.value != null) map[row.key] = row.value
      }
      return map
    },
  })

  // Seed the form from saved settings exactly once, when they first load, so we
  // never clobber the admin's in-progress edits on a background refetch.
  const seeded = useRef(false)
  useEffect(() => {
    if (!savedSettings || seeded.current) return
    seeded.current = true
    form.reset({
      company: savedSettings['limo_anywhere_company'] ?? '',
      username: savedSettings['limo_anywhere_username'] ?? '',
      password: '',
      createAs:
        savedSettings['limo_anywhere_create_as'] === 'quote'
          ? 'quote'
          : 'reservation',
      pointToPoint: savedSettings['limo_anywhere_service_point_to_point'] ?? '',
      hourly: savedSettings['limo_anywhere_service_hourly'] ?? '',
      syncBookings: savedSettings['limo_anywhere_sync_bookings'] !== 'false',
    })
  }, [savedSettings, form])

  // "stored ✓" should reflect a password actually on file, not just the last
  // run's result (which is absent if the first save errored at the fetch stage).
  const passwordStored =
    Boolean(result.passwordStored) ||
    Boolean(savedSettings?.['limo_anywhere_password'])

  // LA vehicle types for the mapping dropdowns (refreshed after each fetch).
  const { data: laTypes } = useQuery({
    queryKey: ['onboarding-la-vehicle-types', accountId, result.lastSyncedAt],
    enabled: needsReview || isDone,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('limo_anywhere_vehicle_types')
        .select('id, name, code')
        .eq('account_id', accountId)
        .order('name')
      if (error) throw new Error(error.message)
      return data as LaVehicleTypeRow[]
    },
  })

  const suggestions = useMemo(
    () => result.suggestedMappings ?? [],
    [result.suggestedMappings]
  )

  const unmappedLaTypes = useMemo(
    () => result.unmappedLaTypes ?? [],
    [result.unmappedLaTypes]
  )

  // When the account has no vehicles, creating drafts is the primary action so
  // default every unmapped type to checked; otherwise default to none checked
  // (the admin opts in per type). Keyed on lastSyncedAt so a re-fetch re-seeds.
  const hasVehicles = result.hasVehicles !== false
  const defaultCreateSelection = useMemo(() => {
    if (hasVehicles) return new Set<string>()
    return new Set(unmappedLaTypes.map((t) => t.dbId))
  }, [hasVehicles, unmappedLaTypes])

  const [createSelectionState, setCreateSelectionState] = useState<{
    syncKey?: string
    selected: Set<string>
  }>({ selected: new Set() })

  // Derive the active selection: re-seed from defaults whenever a new fetch
  // arrives (lastSyncedAt changes), otherwise use the admin's edits.
  const createSelection =
    createSelectionState.syncKey === result.lastSyncedAt
      ? createSelectionState.selected
      : defaultCreateSelection

  const toggleCreate = (dbId: string, checked: boolean) => {
    const next = new Set(createSelection)
    if (checked) next.add(dbId)
    else next.delete(dbId)
    setCreateSelectionState({ syncKey: result.lastSyncedAt, selected: next })
  }

  // Only explicit admin overrides are stored; the effective selection for a
  // row is `override ?? suggestion`. Deriving (rather than seeding via an
  // effect) keeps the suggestions as the source of truth and avoids a
  // setState-in-effect cascade.
  const [overrides, setOverrides] = useState<Record<string, string | null>>({})

  const effectiveMapping = (s: SuggestedMapping): string | null =>
    s.vehicleId in overrides ? overrides[s.vehicleId] : s.laVehicleTypeDbId

  // useWatch (vs form.watch) is the memoization-safe subscription API — it
  // keeps the React Compiler happy when feeding controlled inputs.
  const createAsValue = useWatch({ control: form.control, name: 'createAs' })
  const syncBookingsValue = useWatch({
    control: form.control,
    name: 'syncBookings',
  })

  const saveAndFetch = form.handleSubmit((values) => {
    invokeStep.mutate(
      {
        functionName: 'onboard-limo-anywhere',
        accountId,
        params: {
          action: 'save_and_fetch',
          credentials: {
            company: values.company,
            username: values.username,
            // Only send a password when the admin typed one (empty = keep).
            ...(values.password ? { password: values.password } : {}),
          },
          serviceIds: {
            pointToPoint: values.pointToPoint || undefined,
            hourly: values.hourly || undefined,
          },
          createAs: values.createAs,
          syncBookings: values.syncBookings,
        },
      },
      {
        onSuccess: () => form.setValue('password', ''),
        onError: (error) =>
          toast({
            title: 'Limo Anywhere fetch failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )
  })

  const confirmMapping = () => {
    // Map-to-existing rows only when the account actually has vehicles.
    const mappings = hasVehicles
      ? suggestions.map((s) => ({
          vehicleId: s.vehicleId,
          laVehicleTypeDbId: effectiveMapping(s),
        }))
      : []
    const creates = [...createSelection].map((dbId) => ({
      laVehicleTypeDbId: dbId,
    }))
    invokeStep.mutate(
      {
        functionName: 'onboard-limo-anywhere',
        accountId,
        params: { action: 'confirm_mapping', mappings, creates },
      },
      {
        onError: (error) =>
          toast({
            title: 'Mapping failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )
  }

  return (
    <StepCard
      task={task}
      manualFallback={
        <p>
          If the automation service is unavailable, the client can configure
          Limo Anywhere directly under Settings &gt; Integrations &gt; Limo
          Anywhere in their dashboard (Company ID, Username, Password, then
          Fetch Vehicle Types and map). This step is safe to re-run later.
        </p>
      }
    >
      {isDone ? (
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <span>{doneSummary(result)}</span>
        </div>
      ) : null}

      {!isDone && (
        <form onSubmit={saveAndFetch} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="la-company">Company ID</Label>
              <Input id="la-company" {...form.register('company')} />
              {form.formState.errors.company && (
                <p className="text-xs text-red-600">
                  {form.formState.errors.company.message}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="la-username">Username</Label>
              <Input id="la-username" {...form.register('username')} />
              {form.formState.errors.username && (
                <p className="text-xs text-red-600">
                  {form.formState.errors.username.message}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="la-password">
              Password
              {passwordStored && (
                <span className="ml-2 text-xs text-green-600">stored ✓</span>
              )}
            </Label>
            <Input
              id="la-password"
              type="password"
              autoComplete="off"
              placeholder={passwordStored ? 'Leave blank to keep stored password' : ''}
              {...form.register('password')}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label>Create As</Label>
              <Select
                value={createAsValue}
                onValueChange={(value) =>
                  form.setValue('createAs', value as 'reservation' | 'quote')
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reservation">Reservation</SelectItem>
                  <SelectItem value="quote">Quote</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="la-p2p">Point-to-Point Service ID</Label>
              <Input
                id="la-p2p"
                placeholder="Optional"
                {...form.register('pointToPoint')}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="la-hourly">Hourly Service ID</Label>
              <Input
                id="la-hourly"
                placeholder="Optional"
                {...form.register('hourly')}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="la-sync"
              checked={syncBookingsValue}
              onCheckedChange={(checked) =>
                form.setValue('syncBookings', checked)
              }
            />
            <Label htmlFor="la-sync" className="text-sm font-normal">
              Sync bookings to Limo Anywhere after payment
            </Label>
          </div>

          <Button type="submit" size="sm" disabled={invokeStep.isPending}>
            {invokeStep.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : needsReview ? (
              <RefreshCw className="mr-2 h-4 w-4" />
            ) : (
              <Search className="mr-2 h-4 w-4" />
            )}
            {needsReview ? 'Re-fetch / re-map' : 'Save & fetch vehicle types'}
          </Button>
        </form>
      )}

      {needsReview && (
        <div className="space-y-4">
          {hasVehicles && suggestions.length > 0 && (
            <MappingTable
              suggestions={suggestions}
              laTypes={laTypes ?? []}
              valueFor={effectiveMapping}
              onChange={(vehicleId, value) =>
                setOverrides((prev) => ({ ...prev, [vehicleId]: value }))
              }
            />
          )}

          {unmappedLaTypes.length > 0 && (
            <CreateDraftsSection
              unmappedLaTypes={unmappedLaTypes}
              selection={createSelection}
              onToggle={toggleCreate}
            />
          )}

          {suggestions.length === 0 && unmappedLaTypes.length === 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Fetched {result.vehicleTypeCount ?? 0} Limo Anywhere vehicle type
              {(result.vehicleTypeCount ?? 0) === 1 ? '' : 's'}, and all of them
              are already linked to platform vehicles. Nothing to confirm.
            </div>
          )}

          {(suggestions.length > 0 || unmappedLaTypes.length > 0) && (
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={confirmMapping}
                disabled={invokeStep.isPending}
              >
                {invokeStep.isPending && (
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                )}
                Confirm
              </Button>
            </div>
          )}
        </div>
      )}
    </StepCard>
  )
}

interface MappingTableProps {
  suggestions: SuggestedMapping[]
  laTypes: LaVehicleTypeRow[]
  valueFor: (s: SuggestedMapping) => string | null
  onChange: (vehicleId: string, value: string | null) => void
}

function MappingTable({
  suggestions,
  laTypes,
  valueFor,
  onChange,
}: MappingTableProps) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">
        Map platform vehicles to Limo Anywhere types
      </p>
      <div className="overflow-hidden rounded-lg border">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Platform Vehicle
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Limo Anywhere Vehicle Type
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {suggestions.map((s) => (
              <tr key={s.vehicleId} className="hover:bg-muted/50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium">{s.vehicleName}</div>
                  <div className="text-xs text-muted-foreground">
                    {CONFIDENCE_HINT[s.confidence]}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Select
                    value={valueFor(s) ?? 'none'}
                    onValueChange={(value) =>
                      onChange(s.vehicleId, value === 'none' ? null : value)
                    }
                  >
                    <SelectTrigger className="w-[250px]">
                      <SelectValue placeholder="Select vehicle type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not mapped</SelectItem>
                      {laTypes.map((vt) => (
                        <SelectItem key={vt.id} value={vt.id}>
                          {vt.name}
                          {vt.code ? ` (${vt.code})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface CreateDraftsSectionProps {
  unmappedLaTypes: UnmappedLaType[]
  selection: ReadonlySet<string>
  onToggle: (dbId: string, checked: boolean) => void
}

function CreateDraftsSection({
  unmappedLaTypes,
  selection,
  onToggle,
}: CreateDraftsSectionProps) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">
        Create draft vehicles from Limo Anywhere types
      </p>
      <div className="divide-y rounded-lg border">
        {unmappedLaTypes.map((t) => (
          <label
            key={t.dbId}
            className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-muted/50"
          >
            <Checkbox
              checked={selection.has(t.dbId)}
              onCheckedChange={(checked) => onToggle(t.dbId, checked === true)}
            />
            <span className="text-sm font-medium">
              {t.name}
              {t.code ? (
                <span className="text-muted-foreground"> ({t.code})</span>
              ) : null}
            </span>
          </label>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Creates inactive draft vehicles linked to Limo Anywhere — add pricing
        &amp; photos in Vehicles, then activate.
      </p>
    </div>
  )
}

/** Human summary for the done state, including any created draft vehicles. */
function doneSummary(result: LimoAnywhereResult): string {
  const mapped = result.mappedCount ?? 0
  const created = result.createdCount ?? 0
  const mappedText = `${mapped} vehicle${mapped === 1 ? '' : 's'} mapped`
  if (created > 0) {
    return `${mappedText}, ${created} draft vehicle${
      created === 1 ? '' : 's'
    } created.`
  }
  return `${mappedText} to Limo Anywhere types.`
}
